import { Timestamp } from 'firebase/firestore';

import {
  generateFieldId,
  generateGroupId,
} from '../utils/ids';

/** Well-known field ID for the General Comments field — same across all tryouts. */
export const GENERAL_COMMENTS_FIELD_ID = 'gc_general';
/** Well-known group ID for the General Comments group. */
export const GENERAL_COMMENTS_GROUP_ID = 'gc_group';

export type FieldType = 'skill' | 'heading' | 'generalComments';

export interface SkillConfig {
  rating: boolean;
  comment: boolean;
  photo: boolean;
  video: boolean;
  maxRating: number;
}

export interface FieldConfig {
  skill?: SkillConfig;
}

export interface FormField {
  id: string;
  label: string;
  type: FieldType;
  order: number;
  config?: FieldConfig;
}

export interface FormGroup {
  id: string;
  label: string;
  order: number;
  fields: FormField[];
}

export interface FormSchema {
  groups: FormGroup[];
}

/** Skill rating fields never use a max above this (UI + persisted schema). */
export const RATING_MAX_CAP = 5;

export function defaultSkillConfig(): SkillConfig {
  return {
    rating: true,
    comment: false,
    photo: false,
    video: false,
    maxRating: RATING_MAX_CAP,
  };
}

function clampMaxRating(n: number): number {
  return Math.min(RATING_MAX_CAP, Math.max(1, n));
}

function normalizeSkillConfig(s: SkillConfig): SkillConfig {
  return {
    rating: s.rating,
    comment: s.comment,
    photo: s.photo,
    video: s.video ?? false,
    maxRating: clampMaxRating(s.maxRating),
  };
}

/** Skill row config for editor / rendering; fills defaults for incomplete data. */
export function getSkillConfig(field: FormField): SkillConfig {
  if (field.type !== 'skill') {
    return defaultSkillConfig();
  }
  const s = field.config?.skill;
  if (!s) {
    return defaultSkillConfig();
  }
  return normalizeSkillConfig(s);
}

function normalizeField(field: FormField): FormField {
  if (field.type === 'heading') {
    return {
      id: field.id,
      label: field.label,
      order: field.order,
      type: 'heading',
    };
  }

  if (field.type === 'skill') {
    if (field.config?.skill) {
      return {
        ...field,
        type: 'skill',
        config: { skill: normalizeSkillConfig(field.config.skill) },
      };
    }
    return {
      ...field,
      type: 'skill',
      config: { skill: defaultSkillConfig() },
    };
  }

  return field;
}

const GENERAL_COMMENTS_GROUP: FormGroup = {
  id: GENERAL_COMMENTS_GROUP_ID,
  label: 'General Comments',
  order: -1,
  fields: [
    {
      id: GENERAL_COMMENTS_FIELD_ID,
      label: 'General Comments',
      type: 'generalComments',
      order: 0,
    },
  ],
};

/** Clamp skill config (e.g. maxRating) and fill defaults for incomplete skill rows.
 *  Always ensures the General Comments group is present as the first group. */
export function normalizeFormSchema(schema: FormSchema): FormSchema {
  const withoutGeneralComments = schema.groups.filter(
    (g) => g.id !== GENERAL_COMMENTS_GROUP_ID,
  );
  return {
    groups: [
      GENERAL_COMMENTS_GROUP,
      ...withoutGeneralComments.map((g) => ({
        ...g,
        fields: g.fields.map((f) => normalizeField(f)),
      })),
    ],
  };
}

export interface FormTemplate {
  id: string;
  name: string;
  schema: FormSchema;
  /** Set once on first Firestore save; absent for drafts in the editor. */
  createdAt?: Timestamp | null;
  /** Refreshed on every Firestore save; absent for drafts in the editor. */
  lastUpdated?: Timestamp | null;
}

export function createSkillField(order: number): FormField {
  return {
    id: generateFieldId(),
    label: '',
    type: 'skill',
    order,
    config: { skill: defaultSkillConfig() },
  };
}

export function createEmptyField(order: number): FormField {
  return createSkillField(order);
}

export function createHeadingField(order: number): FormField {
  return {
    id: generateFieldId(),
    label: '',
    type: 'heading',
    order,
  };
}

export function createEmptyGroup(order: number): FormGroup {
  return {
    id: generateGroupId(),
    label: '',
    order,
    fields: [],
  };
}

/** True for skill rows (not section headings). */
export function isSkillField(field: FormField): boolean {
  return field.type === 'skill';
}

/** Every section label and every skill/heading label must be non-empty (after trim). */
export function schemaHasAllLabels(schema: FormSchema): boolean {
  for (const g of schema.groups) {
    if (!g.label.trim()) {
      return false;
    }
    for (const f of g.fields) {
      if (!f.label.trim()) {
        return false;
      }
    }
  }
  return true;
}
